import { Box, Link, Typography, TypographyProps } from '@mui/material';
import OverlineSection from './OverlineSection';
import ArticleCoverImage from '../Article/ArticleCoverImage';
import { Link as RouterLink } from 'react-router';
import ArticleMeta from '../../types/ArticleMeta';
import { CardGTMData, getClampedTextStyles } from './CardUtils';
import {
  articleCardClickTarget,
  sendArticleCardClickToGTM,
} from '../../utilities/gtm';
import AuthorList from '../Article/AuthorList';

interface HighlightedMinimizedArticleCardProps {
  meta: ArticleMeta;
  gtmData?: CardGTMData;
  imageAspectRatio?: number;
  heading?: TypographyProps['component'];
}

export default function HighlightedMinimizedArticleCard({
  meta,
  gtmData = {},
  imageAspectRatio,
  heading = 'h2',
}: Readonly<HighlightedMinimizedArticleCardProps>) {
  return (
    <Box component='article'>
      <Box
        sx={{
          marginY: 0.5,
        }}
      >
        <OverlineSection type={meta.type} openContent={meta.isPublic} />
      </Box>
      <ArticleCoverImage
        src={meta.coverImage}
        category={meta.categories.primary}
        aspectRatio={imageAspectRatio}
        isVideo={meta.hasVideoContent()}
      />
      <Link
        component={RouterLink}
        to={meta.getRoute()}
        title={meta.title}
        data-testid='article-card-link'
        onClick={() =>
          sendArticleCardClickToGTM(
            meta,
            articleCardClickTarget.ARTICLE,
            gtmData.location,
            gtmData.index,
          )
        }
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          paddingY: 1,
          justifyContent: 'space-between',
          textDecoration: 'none',
          color: 'text.primary',
          '&:hover': { color: 'primary.main' },
        }}
      >
        <Typography
          component={heading}
          variant='h6'
          sx={{
            ...getClampedTextStyles(3),
            hyphens: 'auto',
            textWrap: 'balance',
          }}
        >
          {meta.title}
        </Typography>
      </Link>
      {meta.authors.length > 0 && (
        <AuthorList
          variant='caption'
          authors={meta.authors}
          onClick={() =>
            sendArticleCardClickToGTM(
              meta,
              articleCardClickTarget.AUTHOR,
              gtmData.location,
              gtmData.index,
            )
          }
          TypographyProps={{ paddingY: 0.25 }}
        />
      )}
    </Box>
  );
}
